import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowRight,
  CheckCircle,
  Clock,
  Users,
  Target,
  Phone,
  Mail,
  Calendar,
  MessageSquare,
} from "lucide-react";

interface GetStartedDialogProps {
  title?: string;
  description?: string;
  category?: string;
  buttonText?: string;
  variant?:
    | "default"
    | "outline"
    | "secondary"
    | "ghost"
    | "link"
    | "destructive";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}

const GetStartedDialog = ({
  title = "Get Started",
  description = "Start your digital transformation journey with us",
  category = "general",
  buttonText = "Get Started",
  variant = "default",
  size = "default",
  className = "",
}: GetStartedDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    projectType: "",
    budget: "",
    timeline: "",
    description: "",
  });
  const handlePhoneChange = (value: string) => {
    let sanitized = value.replace(/[^\d+]/g, "");

    if (sanitized.includes("+")) {
      sanitized =
        (sanitized.startsWith("+") ? "+" : "") +
        sanitized.replace(/\+/g, "").replace(/^\+/, "");
    }

    handleInputChange("phone", sanitized);
  };
  const { toast } = useToast();

  const POWER_AUTOMATE_URL =
    "https://88e5ea1ee6d3e02d8fdccaf1b47550.d7.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/344a673002c74a2fbeaa80ac4eeb7b0b/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=IUzhHpn5vXUWrGYIUGmfdML4CtVCgt8QOFT1jqtEjSM";

  const [loading, setLoading] = useState(false);

  // VALIDATION
  const isFormValid = () => {
    return (
      formData.name.trim() &&
      formData.email.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
      formData.description.trim()
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) {
      toast({
        title: "Missing Required Fields",
        description: "Please fill all required fields correctly.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const submittedAt = new Date();

      const payload = {
        subject: `New Get Started Request | ${formData.name} | ${formData.company || "No Company"}`,

        message: `
        <div style="font-family: Arial, sans-serif; line-height: 1.6;">
          <h2>New Get Started / Consultation Request</h2>

          <h3>Client Information</h3>
          <p>
            <strong>Full Name:</strong> ${formData.name}<br>
            <strong>Email Address:</strong> ${formData.email}<br>
            <strong>Company:</strong> ${formData.company || "Not Provided"}<br>
            <strong>Phone Number:</strong> ${formData.phone || "Not Provided"}<br>
            <strong>Category:</strong> ${category || "General"}
          </p>

          <h3>Project Requirements</h3>
          <p>
            <strong>Project Type:</strong> ${formData.projectType || "Not Selected"}<br>
            <strong>Budget Range:</strong> ${formData.budget || "Not Selected"}<br>
            <strong>Timeline:</strong> ${formData.timeline || "Not Selected"}
          </p>

          <h3>Project Description</h3>
          <p>${formData.description.replace(/\n/g, "<br>")}</p>

          <hr>

          <p>
            <strong>Submitted On:</strong> ${submittedAt.toLocaleString()}<br>
            <strong>Source:</strong> Smartborn Get Started Dialog
          </p>
        </div>
      `.trim(),

        fullName: formData.name,
        email: formData.email,
        company: formData.company || "",
        phone: formData.phone || "",
        projectType: formData.projectType || "",
        budget: formData.budget || "",
        timeline: formData.timeline || "",
        description: formData.description,
        category,
        submittedAt: submittedAt.toISOString(),
        source: "Smartborn Get Started Dialog",
      };

      const response = await fetch(POWER_AUTOMATE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      toast({
        title: "Request Submitted Successfully!",
        description:
          "Our team will contact you within 24 hours to discuss your project.",
      });

      setIsOpen(false);

      setFormData({
        name: "",
        email: "",
        company: "",
        phone: "",
        projectType: "",
        budget: "",
        timeline: "",
        description: "",
      });
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: "Failed to send request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className={className}>
          {buttonText}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            {title}
          </DialogTitle>
          <p className="text-muted-foreground text-center">{description}</p>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Left Column - Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    placeholder="Company"
                    value={formData.company}
                    onChange={(e) =>
                      handleInputChange("company", e.target.value)
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    inputMode="tel"
                    maxLength={15}
                    value={formData.phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    placeholder="+91 9876543210"
                    pattern="^\+?[0-9]{7,15}$"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="projectType">Project Type</Label>
                <Select
                  value={formData.projectType}
                  onValueChange={(value) =>
                    handleInputChange("projectType", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="web-development">
                      Web Development
                    </SelectItem>
                    <SelectItem value="mobile-app">
                      Mobile App Development
                    </SelectItem>
                    <SelectItem value="enterprise-software">
                      Enterprise Software
                    </SelectItem>
                    <SelectItem value="sap-implementation">
                      SAP Implementation
                    </SelectItem>
                    <SelectItem value="salesforce-customization">
                      Salesforce Customization
                    </SelectItem>
                    <SelectItem value="workday-implementation">
                      Workday Implementation
                    </SelectItem>
                    <SelectItem value="cloud-migration">
                      Cloud Migration
                    </SelectItem>
                    <SelectItem value="ai-ml">AI/ML Solutions</SelectItem>
                    <SelectItem value="staff-augmentation">
                      Staff Augmentation
                    </SelectItem>
                    <SelectItem value="consulting">
                      Consulting Services
                    </SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="budget">Budget Range</Label>
                  <Select
                    value={formData.budget}
                    onValueChange={(value) =>
                      handleInputChange("budget", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select budget range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10k-25k">$10,000 - $25,000</SelectItem>
                      <SelectItem value="25k-50k">$25,000 - $50,000</SelectItem>
                      <SelectItem value="50k-100k">
                        $50,000 - $100,000
                      </SelectItem>
                      <SelectItem value="100k-250k">
                        $100,000 - $250,000
                      </SelectItem>
                      <SelectItem value="250k+">$250,000+</SelectItem>
                      <SelectItem value="discuss">Prefer to discuss</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="timeline">Timeline</Label>
                  <Select
                    value={formData.timeline}
                    onValueChange={(value) =>
                      handleInputChange("timeline", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select timeline" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asap">ASAP</SelectItem>
                      <SelectItem value="1-3-months">1-3 months</SelectItem>
                      <SelectItem value="3-6-months">3-6 months</SelectItem>
                      <SelectItem value="6-12-months">6-12 months</SelectItem>
                      <SelectItem value="12+months">12+ months</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Project Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  placeholder="Tell us about your project requirements, goals, and any specific challenges you're facing..."
                  rows={4}
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={loading || !isFormValid()}
              >
                {loading ? "Submitting..." : "Submit Request"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
          </div>

          {/* Right Column - Process & Contact Info */}
          <div className="space-y-6">
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Our Process
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 text-primary rounded-full p-1.5 text-xs font-bold min-w-[24px] h-6 flex items-center justify-center">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold">Initial Consultation</h4>
                    <p className="text-sm text-muted-foreground">
                      Free 30-minute consultation to understand your
                      requirements
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 text-primary rounded-full p-1.5 text-xs font-bold min-w-[24px] h-6 flex items-center justify-center">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold">Proposal & Planning</h4>
                    <p className="text-sm text-muted-foreground">
                      Detailed proposal with timeline, budget, and technical
                      approach
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 text-primary rounded-full p-1.5 text-xs font-bold min-w-[24px] h-6 flex items-center justify-center">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold">Development & Delivery</h4>
                    <p className="text-sm text-muted-foreground">
                      Agile development with regular updates and feedback cycles
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  What You Get
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-primary" />
                  <span className="text-sm">
                    Free consultation & technical assessment
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-secondary" />
                  <span className="text-sm">Detailed project proposal</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-primary" />
                  <span className="text-sm">Risk-free proof of concept</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-secondary" />
                  <span className="text-sm">Dedicated project manager</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-primary" />
                  <span className="text-sm">
                    24/7 support during development
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  Response Time
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <span className="text-sm">
                    Initial response: Within 2 hours
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-secondary" />
                  <span className="text-sm">
                    Consultation scheduled: Within 24 hours
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-primary" />
                  <span className="text-sm">
                    Proposal delivery: 2-3 business days
                  </span>
                </div>
              </CardContent>
            </Card>

            <div className="text-center p-4 bg-gradient-subtle rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">
                Need immediate assistance?
              </p>
              <div className="flex justify-center gap-4">
                <a
                  href="tel:+91 98869 28201"
                  className="flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <Phone className="h-4 w-4" />
                  {/* +1 (555) 012-3456 */}
                  +91 98869 28201
                </a>
                <a
                  href="mailto:contact@smartborn.io"
                  className="flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <Mail className="h-4 w-4" />
                  contact@smartborn.io
                </a>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GetStartedDialog;
